import React, { useState, useEffect } from 'react';
import { useNavigate, useLocation, useSearchParams } from 'react-router-dom';
import { Helmet } from 'react-helmet';
import MarketplaceHeader from '../../components/navigation/MarketplaceHeader';
import Icon from '../../components/AppIcon';
import { trackEvent } from '../../hooks/useGoogleAnalytics';
import { categoryService } from '../../services/categoryService';
import { serviceService } from '../../services/serviceService';
import PopularCategories from '../service-catalog-home/components/PopularCategories';
import { trackCategoryBrowse, trackCategorySelected, trackSubcategorySelected, trackServiceImpressions, trackCategoryEngagement,  } from '../../utils/analytics';

const ServiceCategoryBrowse = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const [searchParams] = useSearchParams();
  const [isLoading, setIsLoading] = useState(true);
  const [allServices, setAllServices] = useState([]);
  const [categories, setCategories] = useState([]);
  const [error, setError] = useState('');
  const [selectedCategory, setSelectedCategory] = useState(null);

  // Subcategory data with detailed descriptions
  const subcategoryData = {
    'Tax & Accounting Services': [
    {
      id: 'tax-1',
      name: 'GST Registration & Filing',
      description: 'Complete GST registration services including application filing, documentation support, and monthly/quarterly return filing. Expert guidance on input tax credit, reverse charge mechanism, and compliance requirements.',
      icon: 'FileText',
      image: "https://img.rocket.new/generatedImages/rocket_gen_img_18a16e8f7-1772528862561.png",
      features: ['GST Registration', 'Monthly Returns', 'Annual Returns', 'Input Tax Credit'],
      providers: 120
    },
    {
      id: 'tax-2',
      name: 'Income Tax Planning',
      description: 'Strategic tax planning services to minimize tax liability legally. Includes investment planning under Section 80C, 80D, capital gains optimization, and advance tax calculation with expert consultation.',
      icon: 'Calculator',
      image: 'https://images.unsplash.com/photo-1554224311-beee460c201f?w=800&h=600&fit=crop',
      features: ['Tax Saving Strategies', 'Investment Planning', 'Advance Tax', 'Tax Audit'],
      providers: 95
    },
    {
      id: 'tax-3',
      name: 'Bookkeeping & Accounting',
      description: 'Professional bookkeeping services including daily transaction recording, ledger maintenance, bank reconciliation, and financial statement preparation. Cloud-based accounting solutions with real-time reporting.',
      icon: 'BookOpen',
      image: 'https://images.unsplash.com/photo-1554224311-beee460c201f?w=800&h=600&fit=crop',
      features: ['Daily Bookkeeping', 'Bank Reconciliation', 'Financial Statements', 'Cloud Accounting'],
      providers: 150
    },
    {
      id: 'tax-4',
      name: 'TDS Return Filing',
      description: 'Comprehensive TDS compliance services including quarterly return filing, TDS certificate generation, correction statements, and lower deduction certificate applications. Expert handling of Form 26Q, 24Q, 27Q.',
      icon: 'Receipt',
      image: 'https://images.unsplash.com/photo-1554224311-beee460c201f?w=800&h=600&fit=crop',
      features: ['Quarterly TDS Returns', 'Form 16/16A', 'TDS Certificates', 'Correction Statements'],
      providers: 80
    },
    {
      id: 'tax-5',
      name: 'Payroll Management',
      description: 'End-to-end payroll processing services including salary calculation, statutory compliance (PF, ESI, PT), payslip generation, and annual tax computation. Automated payroll with employee self-service portal.',
      icon: 'Users',
      image: 'https://images.unsplash.com/photo-1554224311-beee460c201f?w=800&h=600&fit=crop',
      features: ['Salary Processing', 'PF/ESI Compliance', 'Payslip Generation', 'Form 16 Issuance'],
      providers: 110
    }],

    'Professional Services': [
    {
      id: 'prof-1',
      name: 'Business Consulting',
      description: 'Strategic business consulting services covering market analysis, business model optimization, growth strategy development, and operational efficiency improvement. Expert guidance for startups and established businesses.',
      icon: 'Briefcase',
      image: "https://img.rocket.new/generatedImages/rocket_gen_img_197f8b897-1766749340856.png",
      features: ['Strategy Development', 'Market Analysis', 'Process Optimization', 'Growth Planning'],
      providers: 85
    },
    {
      id: 'prof-2',
      name: 'Financial Advisory',
      description: 'Comprehensive financial advisory services including investment portfolio management, wealth planning, retirement planning, and risk assessment. Personalized financial strategies aligned with your goals.',
      icon: 'TrendingUp',
      image: "https://img.rocket.new/generatedImages/rocket_gen_img_197f8b897-1766749340856.png",
      features: ['Investment Planning', 'Wealth Management', 'Retirement Planning', 'Risk Management'],
      providers: 70
    },
    {
      id: 'prof-3',
      name: 'Management Consulting',
      description: 'Expert management consulting for organizational restructuring, change management, performance improvement, and leadership development. Data-driven insights for better decision making.',
      icon: 'Target',
      image: "https://img.rocket.new/generatedImages/rocket_gen_img_16987e45b-1765126602033.png",
      features: ['Org Restructuring', 'Change Management', 'Performance Metrics', 'Leadership Training'],
      providers: 65
    },
    {
      id: 'prof-4',
      name: 'Project Management',
      description: 'Professional project management services using agile and waterfall methodologies. Includes project planning, resource allocation, timeline management, and stakeholder communication for successful delivery.',
      icon: 'CheckSquare',
      image: 'https://images.unsplash.com/photo-1542744173-8e7e53415bb0?w=800&h=600&fit=crop',
      features: ['Project Planning', 'Resource Management', 'Agile/Scrum', 'Risk Mitigation'],
      providers: 90
    }],

    'Legal & Compliance': [
    {
      id: 'legal-1',
      name: 'Company Registration',
      description: 'Complete company incorporation services including name approval, MOA/AOA drafting, DIN/DSC procurement, and registration certificate. Support for Private Limited, LLP, OPC, and Partnership firms.',
      icon: 'Building',
      image: "https://img.rocket.new/generatedImages/rocket_gen_img_1f9d13084-1775719710557.png",
      features: ['Pvt Ltd Registration', 'LLP Formation', 'Partnership Deed', 'Name Approval'],
      providers: 130
    },
    {
      id: 'legal-2',
      name: 'Trademark & IP',
      description: 'Intellectual property protection services including trademark registration, copyright filing, patent application, and IP portfolio management. Comprehensive search and legal opinion included.',
      icon: 'Award',
      image: "https://img.rocket.new/generatedImages/rocket_gen_img_1d892ee96-1768723747686.png",
      features: ['Trademark Registration', 'Copyright Filing', 'Patent Application', 'IP Search'],
      providers: 75
    },
    {
      id: 'legal-3',
      name: 'Contract Drafting',
      description: 'Professional legal contract drafting and vetting services for employment agreements, vendor contracts, NDA, partnership deeds, and service agreements. Ensures legal compliance and risk mitigation.',
      icon: 'FileSignature',
      image: "https://img.rocket.new/generatedImages/rocket_gen_img_19ede8fea-1767647323259.png",
      features: ['Employment Contracts', 'Vendor Agreements', 'NDA Drafting', 'Contract Review'],
      providers: 95
    },
    {
      id: 'legal-4',
      name: 'Compliance Management',
      description: 'Comprehensive compliance management covering annual filings, board meetings, AGM/EGM conduct, statutory registers maintenance, and regulatory compliance across various acts and regulations.',
      icon: 'Shield',
      image: "https://img.rocket.new/generatedImages/rocket_gen_img_100199d23-1775719712082.png",
      features: ['Annual Filings', 'Board Meetings', 'Statutory Registers', 'ROC Compliance'],
      providers: 85
    },
    {
      id: 'legal-5',
      name: 'Legal Advisory',
      description: 'Expert legal consultation for business disputes, litigation support, legal notice drafting, and general corporate legal matters. Experienced lawyers providing practical legal solutions.',
      icon: 'Scale',
      image: "https://img.rocket.new/generatedImages/rocket_gen_img_11ee13166-1766472898334.png",
      features: ['Legal Consultation', 'Dispute Resolution', 'Notice Drafting', 'Litigation Support'],
      providers: 100
    }],

    'Digital Marketing': [
    {
      id: 'digital-1',
      name: 'SEO Services',
      description: 'Comprehensive search engine optimization services including keyword research, on-page optimization, technical SEO, link building, and local SEO. Improve organic rankings and drive quality traffic.',
      icon: 'Search',
      image: "https://img.rocket.new/generatedImages/rocket_gen_img_134f5ac94-1775670470525.png",
      features: ['Keyword Research', 'On-Page SEO', 'Link Building', 'Local SEO'],
      providers: 140
    },
    {
      id: 'digital-2',
      name: 'Social Media Marketing',
      description: 'Strategic social media management across Facebook, Instagram, LinkedIn, and Twitter. Includes content creation, community management, paid advertising, and performance analytics.',
      icon: 'Share2',
      image: "https://img.rocket.new/generatedImages/rocket_gen_img_1470fbb91-1775719710540.png",
      features: ['Content Creation', 'Community Management', 'Paid Ads', 'Analytics'],
      providers: 160
    },
    {
      id: 'digital-3',
      name: 'Content Marketing',
      description: 'Professional content marketing services including blog writing, article creation, infographic design, video content, and content strategy development. Engaging content that converts.',
      icon: 'Edit',
      image: "https://img.rocket.new/generatedImages/rocket_gen_img_168e1f91e-1769340913600.png",
      features: ['Blog Writing', 'Article Creation', 'Video Content', 'Content Strategy'],
      providers: 125
    },
    {
      id: 'digital-4',
      name: 'PPC Advertising',
      description: 'Pay-per-click advertising management for Google Ads, Facebook Ads, and LinkedIn Ads. Campaign setup, optimization, A/B testing, and ROI tracking for maximum conversion.',
      icon: 'MousePointer',
      image: "https://img.rocket.new/generatedImages/rocket_gen_img_19c13f543-1765872622903.png",
      features: ['Google Ads', 'Facebook Ads', 'Campaign Optimization', 'ROI Tracking'],
      providers: 110
    }],

    'IT & Technology': [
    {
      id: 'it-1',
      name: 'Web Development',
      description: 'Custom website development services including responsive design, e-commerce solutions, CMS integration, and web application development. Modern, fast, and SEO-friendly websites.',
      icon: 'Globe',
      image: "https://img.rocket.new/generatedImages/rocket_gen_img_133004601-1773386590840.png",
      features: ['Responsive Design', 'E-commerce', 'CMS Integration', 'Web Apps'],
      providers: 180
    },
    {
      id: 'it-2',
      name: 'Mobile App Development',
      description: 'Native and cross-platform mobile app development for iOS and Android. Includes UI/UX design, backend integration, app store deployment, and maintenance support.',
      icon: 'Smartphone',
      image: "https://img.rocket.new/generatedImages/rocket_gen_img_180792f24-1773750464109.png",
      features: ['iOS Development', 'Android Development', 'Cross-Platform', 'App Store Launch'],
      providers: 150
    },
    {
      id: 'it-3',
      name: 'Cloud Solutions',
      description: 'Cloud infrastructure setup and management including AWS, Azure, Google Cloud. Migration services, cloud security, backup solutions, and cost optimization.',
      icon: 'Cloud',
      image: "https://img.rocket.new/generatedImages/rocket_gen_img_1156a8060-1766529779372.png",
      features: ['Cloud Migration', 'AWS/Azure Setup', 'Cloud Security', 'Cost Optimization'],
      providers: 95
    },
    {
      id: 'it-4',
      name: 'Cybersecurity',
      description: 'Comprehensive cybersecurity services including security audits, penetration testing, vulnerability assessment, firewall setup, and security compliance. Protect your digital assets.',
      icon: 'Lock',
      image: "https://img.rocket.new/generatedImages/rocket_gen_img_1a1ee97f6-1775719710212.png",
      features: ['Security Audit', 'Penetration Testing', 'Firewall Setup', 'Compliance'],
      providers: 70
    },
    {
      id: 'it-5',
      name: 'IT Support & Maintenance',
      description: 'Ongoing IT support services including helpdesk support, system maintenance, software updates, network management, and technical troubleshooting. 24/7 support available.',
      icon: 'Headphones',
      image: "https://img.rocket.new/generatedImages/rocket_gen_img_13a2af1bc-1769450330774.png",
      features: ['24/7 Helpdesk', 'System Maintenance', 'Network Management', 'Troubleshooting'],
      providers: 130
    }],

    'HR & Recruitment': [
    {
      id: 'hr-1',
      name: 'Talent Acquisition',
      description: 'End-to-end recruitment services including job posting, candidate sourcing, screening, interviewing, and onboarding. Access to extensive talent pool across industries and experience levels.',
      icon: 'UserPlus',
      image: "https://img.rocket.new/generatedImages/rocket_gen_img_1f993c86f-1765003275610.png",
      features: ['Candidate Sourcing', 'Screening', 'Interview Coordination', 'Onboarding'],
      providers: 120
    },
    {
      id: 'hr-2',
      name: 'HR Compliance',
      description: 'HR compliance management including labor law compliance, employee contracts, policy documentation, PF/ESI registration, and statutory compliance. Ensure legal adherence in all HR matters.',
      icon: 'FileCheck',
      image: "https://img.rocket.new/generatedImages/rocket_gen_img_1eac9b91a-1772792247465.png",
      features: ['Labor Law Compliance', 'Policy Documentation', 'PF/ESI Registration', 'Contracts'],
      providers: 85
    },
    {
      id: 'hr-3',
      name: 'Performance Management',
      description: 'Performance management system implementation including KPI setting, performance reviews, 360-degree feedback, goal tracking, and employee development plans. Data-driven performance insights.',
      icon: 'BarChart',
      image: "https://img.rocket.new/generatedImages/rocket_gen_img_137be6364-1773386594226.png",
      features: ['KPI Setting', 'Performance Reviews', '360 Feedback', 'Development Plans'],
      providers: 75
    },
    {
      id: 'hr-4',
      name: 'Training & Development',
      description: 'Corporate training programs covering leadership development, technical skills, soft skills, and compliance training. Customized training modules with certification and progress tracking.',
      icon: 'GraduationCap',
      image: "https://img.rocket.new/generatedImages/rocket_gen_img_10f791952-1775719710571.png",
      features: ['Leadership Training', 'Technical Skills', 'Soft Skills', 'Certification'],
      providers: 90
    }]

  };

  useEffect(() => {
    loadData();
  }, []);

  const loadData = async () => {
    try {
      setIsLoading(true);
      setError('');

      // Fetch all services and categories
      const [servicesData, categoriesData] = await Promise.all([
      serviceService?.getAll(),
      categoryService?.getAll()]
      );

      setAllServices(servicesData || []);
      setCategories(categoriesData || []);

    } catch (err) {
      console.error('Error loading data:', err);
      setError(err?.message || 'Failed to load data');
    } finally {
      setIsLoading(false);
    }
  };

  useEffect(() => {
    trackEvent('browse_services_view', {
      page: 'service-category-browse',
      total_services: allServices?.length
    });
    // Funnel Stage 2: Category browse page viewed
    trackCategoryBrowse('all', 'browse_page', { total_services: allServices?.length });
    if (allServices?.length > 0) {
      trackServiceImpressions(allServices, 'browse_all_services');
    }
  }, [allServices]);

  const handleServiceClick = (service) => {
    trackEvent('service_standee_click', {
      service_id: service?.id,
      service_name: service?.name,
      category_id: service?.categoryId,
      page: 'service-category-browse'
    });
    navigate('/digital-service-standee', {
      state: {
        subcategoryId: service?.subcategoryId,
        subcategoryName: service?.subcategory?.name,
        categoryId: service?.categoryId,
        categoryName: service?.category?.name
      }
    });
  };

  const handleCategoryClick = (category) => {
    trackEvent('category_explore_click', {
      category_id: category?.id,
      category_name: category?.name,
      page: 'service-category-browse'
    });
    // Journey: category selected
    trackCategorySelected(category?.name, category?.id, 'browse_page');
    trackCategoryEngagement(category?.name, category?.id, 'click');

    // Toggle subcategory view
    if (selectedCategory?.id === category?.id) {
      setSelectedCategory(null);
    } else {
      setSelectedCategory(category);
      // Scroll to subcategories section
      setTimeout(() => {
        document.getElementById('subcategories-section')?.scrollIntoView({ behavior: 'smooth', block: 'start' });
      }, 100);
    }
  };

  const handleSubcategoryClick = (subcategory) => {
    trackEvent('subcategory_click', {
      category_id: selectedCategory?.id,
      category_name: selectedCategory?.name,
      subcategory_id: subcategory?.id,
      subcategory_name: subcategory?.name,
      page: 'service-category-browse'
    });
    // Journey: subcategory selected → entering service detail funnel
    trackSubcategorySelected(subcategory?.name, subcategory?.id, selectedCategory?.name);

    // Navigate to service standee with complete subcategory data
    navigate('/digital-service-standee', {
      state: {
        subcategoryId: subcategory?.id,
        subcategoryName: subcategory?.name,
        categoryId: selectedCategory?.id,
        categoryName: selectedCategory?.name,
        subcategoryData: subcategory // Pass complete subcategory object
      }
    });
  };

  if (error) {
    return (
      <div className="min-h-screen bg-background">
        <Helmet>
          <title>{error}</title>
          <meta name="description" content={error} />
          <meta name="keywords" content={error} />
          <meta name="robots" content="noindex, nofollow" />
          <link rel="canonical" href="/" />
        </Helmet>
        <MarketplaceHeader />
        <div className="max-w-[1440px] mx-auto px-4 md:px-6 lg:px-8 py-6 md:py-8 lg:py-10">
          <div className="text-center py-12">
            <div className="text-destructive text-lg mb-4">{error}</div>
            <button
              onClick={() => navigate('/')}
              className="px-6 py-3 bg-primary text-primary-foreground rounded-lg hover:bg-primary/90 transition-colors">

              Return to Home
            </button>
          </div>
        </div>
      </div>);

  }

  // Category images mapping based on reference image
  const categoryImages = {
    'Tax & Accounting Services': 'https://images.unsplash.com/photo-1554224311-beee460c201f?w=800&h=600&fit=crop',
    'Professional Services': 'https://images.unsplash.com/photo-1542744173-8e7e53415bb0?w=800&h=600&fit=crop',
    'Legal & Compliance': 'https://images.unsplash.com/photo-1589829545856-d10d557cf95f?w=800&h=600&fit=crop',
    'Digital Marketing': 'https://images.unsplash.com/photo-1460925895917-afdab827c52f?w=800&h=600&fit=crop',
    'IT & Technology': 'https://images.unsplash.com/photo-1518770660439-4636190af475?w=800&h=600&fit=crop',
    'HR & Recruitment': 'https://images.unsplash.com/photo-1521737711867-e3b97375f902?w=800&h=600&fit=crop'
  };

  // Service standee images
  const serviceImages = {
    'Accounting': 'https://images.unsplash.com/photo-1554224311-beee460c201f?w=800&h=600&fit=crop',
    'Tax': 'https://images.unsplash.com/photo-1554224311-beee460c201f?w=800&h=600&fit=crop',
    'Consulting': 'https://images.unsplash.com/photo-1542744173-8e7e53415bb0?w=800&h=600&fit=crop',
    'Legal': 'https://images.unsplash.com/photo-1589829545856-d10d557cf95f?w=800&h=600&fit=crop',
    'Marketing': 'https://images.unsplash.com/photo-1460925895917-afdab827c52f?w=800&h=600&fit=crop',
    'Technology': 'https://images.unsplash.com/photo-1518770660439-4636190af475?w=800&h=600&fit=crop',
    'IT': 'https://images.unsplash.com/photo-1518770660439-4636190af475?w=800&h=600&fit=crop',
    'HR': 'https://images.unsplash.com/photo-1521737711867-e3b97375f902?w=800&h=600&fit=crop',
    'Recruitment': 'https://images.unsplash.com/photo-1521737711867-e3b97375f902?w=800&h=600&fit=crop'
  };

  const getCategoryImage = (categoryName) => {
    return categoryImages?.[categoryName] || 'https://images.unsplash.com/photo-1542744173-8e7e53415bb0?w=800&h=600&fit=crop';
  };

  const getServiceImage = (service) => {
    if (service?.imageUrl) return service?.imageUrl;

    const categoryName = service?.category?.name || '';
    const serviceName = service?.name || '';

    // Try to match by category or service name keywords
    for (const [key, image] of Object.entries(serviceImages)) {
      if (categoryName?.includes(key) || serviceName?.includes(key)) {
        return image;
      }
    }

    return 'https://images.unsplash.com/photo-1542744173-8e7e53415bb0?w=800&h=600&fit=crop';
  };

  return (
    <div className="min-h-screen bg-background">
      <Helmet>
        <title>{selectedCategory ? `${selectedCategory?.name} Services | ZippyAssist` : 'Browse 2000+ Business Services | ZippyAssist Service Catalog'}</title>
        <meta name="description" content={selectedCategory
          ? `Explore verified ${selectedCategory?.name} service providers on ZippyAssist. Compare options, view pricing, and connect with experts for your business needs.`
          : 'Browse 2000+ intangible business services on ZippyAssist. Tax, Legal, IT, Digital Marketing, HR, Compliance and more — verified providers for UK, US, Canada, UAE businesses.'
        } />
        <meta name="keywords" content={selectedCategory
          ? `${selectedCategory?.name}, business services, outsource ${selectedCategory?.name}, ZippyAssist`
          : 'business services catalog, outsource services, tax filing, legal compliance, digital marketing, IT services, HR services, ZippyAssist'
        } />
        <meta name="robots" content="index, follow" />
        <link rel="canonical" href={selectedCategory
          ? `https://zippyassist.in/service-category-browse?category=${encodeURIComponent(selectedCategory?.name)}`
          : 'https://zippyassist.in/service-category-browse'
        } />

        {/* Open Graph */}
        <meta property="og:type" content="website" />
        <meta property="og:title" content={selectedCategory ? `${selectedCategory?.name} Services | ZippyAssist` : 'Browse 2000+ Business Services | ZippyAssist'} />
        <meta property="og:description" content={selectedCategory
          ? `Explore verified ${selectedCategory?.name} service providers. Compare options and connect with experts for your business.`
          : 'Browse 2000+ intangible business services. Tax, Legal, IT, Digital Marketing, HR & more — verified providers for global businesses.'
        } />
        <meta property="og:url" content={selectedCategory
          ? `https://zippyassist.in/service-category-browse?category=${encodeURIComponent(selectedCategory?.name)}`
          : 'https://zippyassist.in/service-category-browse'
        } />
        <meta property="og:site_name" content="ZippyAssist" />
        <meta property="og:image" content="https://zippyassist.in/assets/images/200x68-1775706855169.jpg" />

        {/* Twitter Card */}
        <meta name="twitter:card" content="summary_large_image" />
        <meta name="twitter:title" content={selectedCategory ? `${selectedCategory?.name} Services | ZippyAssist` : 'Browse 2000+ Business Services | ZippyAssist'} />
        <meta name="twitter:description" content="Browse verified business service providers across 2000+ categories on ZippyAssist." />
        <meta name="twitter:image" content="https://zippyassist.in/assets/images/200x68-1775706855169.jpg" />

        {/* JSON-LD: CollectionPage */}
        <script type="application/ld+json">{JSON.stringify({
          "@context": "https://schema.org",
          "@type": "CollectionPage",
          "name": selectedCategory ? `${selectedCategory?.name} Services` : "ZippyAssist Service Catalog",
          "description": selectedCategory
            ? `Browse verified ${selectedCategory?.name} service providers on ZippyAssist`
            : "Complete catalog of 2000+ intangible business services on ZippyAssist",
          "url": selectedCategory
            ? `https://zippyassist.in/service-category-browse?category=${encodeURIComponent(selectedCategory?.name)}`
            : "https://zippyassist.in/service-category-browse",
          "isPartOf": {
            "@type": "WebSite",
            "name": "ZippyAssist",
            "url": "https://zippyassist.in"
          },
          "breadcrumb": {
            "@type": "BreadcrumbList",
            "itemListElement": selectedCategory ? [
              { "@type": "ListItem", "position": 1, "name": "Home", "item": "https://zippyassist.in" },
              { "@type": "ListItem", "position": 2, "name": "Services", "item": "https://zippyassist.in/service-category-browse" },
              { "@type": "ListItem", "position": 3, "name": selectedCategory?.name, "item": `https://zippyassist.in/service-category-browse?category=${encodeURIComponent(selectedCategory?.name)}` }
            ] : [
              { "@type": "ListItem", "position": 1, "name": "Home", "item": "https://zippyassist.in" },
              { "@type": "ListItem", "position": 2, "name": "Services", "item": "https://zippyassist.in/service-category-browse" }
            ]
          }
        })}</script>
      </Helmet>
      <MarketplaceHeader />
      {/* Hero Section */}
      <div className="bg-gradient-to-br from-primary/10 via-background to-primary/5 py-12 md:py-16 lg:py-20">
        <div className="max-w-[1440px] mx-auto px-4 md:px-6 lg:px-8">
          <div className="text-center max-w-4xl mx-auto">
            <h1 className="text-3xl md:text-4xl lg:text-5xl font-bold text-foreground mb-4">
              Browse Services
            </h1>
            <p className="text-base md:text-lg text-muted-foreground">
              Discover comprehensive business services tailored to your needs. Explore by category or browse all available services.
            </p>
          </div>
        </div>
      </div>
      {isLoading ?
      <div className="max-w-[1440px] mx-auto px-4 md:px-6 lg:px-8 py-8">
          <div className="space-y-12">
            <div className="animate-pulse">
              <div className="h-8 bg-muted rounded w-1/3 mb-8"></div>
              <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-6">
                {[...Array(8)]?.map((_, i) =>
              <div key={i} className="bg-card rounded-xl border border-border p-6 animate-pulse">
                    <div className="w-12 h-12 bg-muted rounded-lg mb-4"></div>
                    <div className="h-6 bg-muted rounded mb-2 w-3/4"></div>
                    <div className="h-4 bg-muted rounded w-full"></div>
                  </div>
              )}
              </div>
            </div>
          </div>
        </div> :

      <>
          {/* Explore Service Categories Section */}
          <div className="bg-background py-12 md:py-16 lg:py-20">
            <div className="max-w-[1440px] mx-auto px-4 md:px-6 lg:px-8">
              <div className="mb-8 md:mb-12">
                <h2 className="text-2xl md:text-3xl lg:text-4xl font-bold text-foreground mb-3 md:mb-4">
                  Explore Service Categories
                </h2>
                <p className="text-base md:text-lg text-muted-foreground max-w-3xl">
                  Browse our comprehensive range of business service categories
                </p>
              </div>

              {categories?.length > 0 ?
            <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 xl:grid-cols-5 gap-4 md:gap-6">
                  {categories?.map((category) =>
              <button
                key={category?.id}
                onClick={() => handleCategoryClick(category)}
                className={`group bg-card rounded-xl border overflow-hidden hover:border-primary hover:shadow-lg transition-all duration-300 text-left ${
                selectedCategory?.id === category?.id ? 'border-primary shadow-lg ring-2 ring-primary/20' : 'border-border'}`
                }>

                      {/* Category Image */}
                      <div className="relative h-40 overflow-hidden">
                        <img
                    src={getCategoryImage(category?.name)}
                    alt={`${category?.name} - Professional business services`}
                    className="w-full h-full object-cover group-hover:scale-110 transition-transform duration-300" />

                        <div className="absolute inset-0 bg-gradient-to-t from-black/60 to-transparent"></div>
                        <div className="absolute top-3 right-3 bg-primary text-primary-foreground px-2 py-1 rounded-full text-xs font-semibold flex items-center gap-1">
                          <Icon name="TrendingUp" size={12} />
                          Trending
                        </div>
                        {selectedCategory?.id === category?.id &&
                  <div className="absolute top-3 left-3 bg-primary text-primary-foreground px-2 py-1 rounded-full text-xs font-semibold flex items-center gap-1">
                            <Icon name="Check" size={12} />
                            Selected
                          </div>
                  }
                      </div>
                      
                      {/* Category Details */}
                      <div className="p-4">
                        <div className="flex items-start gap-3 mb-3">
                          <div className="w-12 h-12 rounded-lg bg-primary/10 flex items-center justify-center flex-shrink-0">
                            <Icon name="Layers" size={24} className="text-primary" />
                          </div>
                          <div className="flex-1 min-w-0">
                            <h3 className="text-base md:text-lg font-semibold text-foreground mb-1 group-hover:text-primary transition-colors line-clamp-2">
                              {category?.name}
                            </h3>
                            <p className="text-xs text-muted-foreground">
                              {category?.serviceCount || '487'} services
                            </p>
                          </div>
                        </div>
                        {category?.description &&
                  <p className="text-xs md:text-sm text-muted-foreground line-clamp-2">
                            {category?.description}
                          </p>
                  }
                      </div>
                    </button>
              )}
                </div> :

            <div className="text-center py-8 text-muted-foreground">
                  No categories available
                </div>
            }
            </div>
          </div>

          {/* Subcategories Section */}
          {selectedCategory && subcategoryData?.[selectedCategory?.name] &&
        <div id="subcategories-section" className="bg-gradient-to-br from-primary/5 via-background to-primary/10 py-12 md:py-16 lg:py-20 border-y border-primary/20">
              <div className="max-w-[1440px] mx-auto px-4 md:px-6 lg:px-8">
                {/* Header with back button */}
                <div className="mb-8 md:mb-12">
                  <button
                onClick={() => setSelectedCategory(null)}
                className="inline-flex items-center gap-2 text-primary hover:text-primary/80 transition-colors mb-4 font-medium">

                    <Icon name="ArrowLeft" size={20} />
                    Back to Categories
                  </button>
                  <div className="flex items-start gap-4 mb-4">
                    <div className="w-16 h-16 rounded-xl bg-primary/10 flex items-center justify-center flex-shrink-0">
                      <Icon name="Layers" size={32} className="text-primary" />
                    </div>
                    <div>
                      <h2 className="text-2xl md:text-3xl lg:text-4xl font-bold text-foreground mb-2">
                        {selectedCategory?.name}
                      </h2>
                      <p className="text-base md:text-lg text-muted-foreground">
                        Explore {subcategoryData?.[selectedCategory?.name]?.length} specialized subcategories with detailed service offerings
                      </p>
                    </div>
                  </div>
                </div>

                {/* Subcategory Standees Grid */}
                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6 md:gap-8">
                  {subcategoryData?.[selectedCategory?.name]?.map((subcategory) =>
              <div
                key={subcategory?.id}
                onClick={() => handleSubcategoryClick(subcategory)}
                className="group bg-card rounded-2xl border border-border overflow-hidden hover:border-primary hover:shadow-2xl transition-all duration-300 cursor-pointer">

                      {/* Subcategory Image */}
                      <div className="relative h-56 overflow-hidden">
                        <img
                    src={subcategory?.image}
                    alt={`${subcategory?.name} - Detailed service information`}
                    className="w-full h-full object-cover group-hover:scale-110 transition-transform duration-500" />

                        <div className="absolute inset-0 bg-gradient-to-t from-black/70 via-black/30 to-transparent"></div>
                        
                        {/* Icon Badge */}
                        <div className="absolute top-4 left-4 w-14 h-14 rounded-xl bg-white/95 backdrop-blur-sm flex items-center justify-center shadow-lg">
                          <Icon name={subcategory?.icon} size={28} className="text-primary" />
                        </div>
                        
                        {/* Provider Count Badge */}
                        <div className="absolute top-4 right-4 bg-primary text-primary-foreground px-3 py-1.5 rounded-full text-xs font-semibold flex items-center gap-1.5 shadow-lg">
                          <Icon name="Users" size={14} />
                          {subcategory?.providers}+ Providers
                        </div>
                        
                        {/* Title Overlay */}
                        <div className="absolute bottom-0 left-0 right-0 p-5">
                          <h3 className="text-xl font-bold text-white mb-1 group-hover:text-primary-foreground transition-colors">
                            {subcategory?.name}
                          </h3>
                        </div>
                      </div>

                      {/* Subcategory Details */}
                      <div className="p-6">
                        {/* Description */}
                        <p className="text-sm text-muted-foreground leading-relaxed mb-5">
                          {subcategory?.description}
                        </p>

                        {/* Features Grid */}
                        <div className="mb-5">
                          <div className="text-xs font-semibold text-foreground mb-3 flex items-center gap-2">
                            <Icon name="CheckCircle" size={16} className="text-primary" />
                            Key Features
                          </div>
                          <div className="grid grid-cols-2 gap-2">
                            {subcategory?.features?.map((feature, idx) =>
                      <div key={idx} className="flex items-center gap-2 text-xs text-muted-foreground">
                                <div className="w-1.5 h-1.5 rounded-full bg-primary flex-shrink-0"></div>
                                <span className="line-clamp-1">{feature}</span>
                              </div>
                      )}
                          </div>
                        </div>

                        {/* CTA Button */}
                        <button className="w-full py-3 px-4 bg-primary/10 text-primary rounded-lg hover:bg-primary hover:text-primary-foreground transition-all duration-300 font-semibold text-sm flex items-center justify-center gap-2 group-hover:gap-3">
                          <span>Explore Services</span>
                          <Icon name="ArrowRight" size={16} />
                        </button>
                      </div>
                    </div>
              )}
                </div>

                {/* Info Banner */}
                <div className="mt-8 p-6 rounded-xl bg-card border border-primary/20">
                  <div className="flex items-start gap-4">
                    <div className="w-12 h-12 rounded-lg bg-primary/10 flex items-center justify-center flex-shrink-0">
                      <Icon name="Info" size={24} className="text-primary" />
                    </div>
                    <div>
                      <h4 className="text-lg font-semibold text-foreground mb-2">
                        Need Help Choosing the Right Service?
                      </h4>
                      <p className="text-sm text-muted-foreground mb-4">
                        Our expert team can help you find the perfect service provider based on your specific requirements. Get personalized recommendations and compare multiple providers.
                      </p>
                      <div className="flex flex-wrap gap-3">
                        <a
                      href="tel:+917039149001"
                      className="inline-flex items-center gap-2 px-4 py-2 bg-primary text-primary-foreground rounded-lg hover:bg-primary/90 transition-colors text-sm font-medium">

                          <Icon name="Phone" size={16} />
                          Call Expert
                        </a>
                        <a
                      href="https://wa.me/917039149001"
                      target="_blank"
                      rel="noopener noreferrer"
                      className="inline-flex items-center gap-2 px-4 py-2 bg-card border border-border text-foreground rounded-lg hover:border-primary hover:text-primary transition-colors text-sm font-medium">

                          <Icon name="MessageCircle" size={16} />
                          WhatsApp
                        </a>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </div>
        }

          {/* Browse by Popular Categories Section */}
          <PopularCategories />

          {/* All Services with Standees Section */}
          <div id="all-services" className="bg-muted/30 py-12 md:py-16 lg:py-20">
            <div className="max-w-[1440px] mx-auto px-4 md:px-6 lg:px-8">
              <div className="mb-8 md:mb-12">
                <h2 className="text-2xl md:text-3xl lg:text-4xl font-bold text-foreground mb-3 md:mb-4">
                  All Services
                </h2>
                <p className="text-base md:text-lg text-muted-foreground max-w-3xl">
                  Browse our complete catalog of {allServices?.length} professional services
                </p>
              </div>

              {allServices?.length > 0 ?
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-6">
                  {allServices?.map((service) =>
              <div
                key={service?.id}
                onClick={() => handleServiceClick(service)}
                className="group bg-card rounded-xl border border-border overflow-hidden hover:border-primary hover:shadow-xl transition-all duration-300 cursor-pointer">

                      {/* Service Image/Standee */}
                      <div className="relative h-48 overflow-hidden">
                        <img
                    src={getServiceImage(service)}
                    alt={`${service?.name} - ${service?.category?.name || 'Professional service'}`}
                    className="w-full h-full object-cover group-hover:scale-110 transition-transform duration-300" />

                        <div className="absolute inset-0 bg-gradient-to-t from-black/50 to-transparent"></div>
                        {service?.isFeatured &&
                  <div className="absolute top-3 right-3 bg-primary text-primary-foreground px-3 py-1 rounded-full text-xs font-semibold flex items-center gap-1">
                            <Icon name="TrendingUp" size={12} />
                            Trending
                          </div>
                  }
                        <div className="absolute bottom-3 left-3 right-3">
                          <div className="text-xs font-medium text-white bg-black/30 backdrop-blur-sm px-2 py-1 rounded inline-block">
                            {service?.category?.name}
                          </div>
                        </div>
                      </div>

                      {/* Service Details */}
                      <div className="p-5">
                        <div className="mb-3">
                          <div className="text-xs font-medium text-primary mb-1">
                            {service?.category?.name}
                          </div>
                          <h3 className="text-lg font-bold text-foreground group-hover:text-primary transition-colors line-clamp-2 mb-2">
                            {service?.name}
                          </h3>
                          <p className="text-sm text-muted-foreground line-clamp-2">
                            {service?.description || 'Professional service solution for your business needs'}
                          </p>
                        </div>

                        {/* Service Meta */}
                        <div className="flex items-center justify-between pt-4 border-t border-border">
                          <div className="flex items-center gap-1 text-sm text-muted-foreground">
                            <Icon name="Star" size={16} className="text-yellow-500 fill-yellow-500" />
                            <span className="font-medium">4.8</span>
                            <span className="text-xs">(120)</span>
                          </div>
                          <div className="flex items-center gap-2 text-primary font-semibold text-sm group-hover:gap-3 transition-all">
                            <span>View Details</span>
                            <Icon name="ArrowRight" size={16} />
                          </div>
                        </div>
                      </div>
                    </div>
              )}
                </div> :

            <div className="text-center py-12">
                  <Icon name="Package" size={64} className="text-muted-foreground mx-auto mb-4" />
                  <div className="text-lg text-muted-foreground mb-2">No services available yet</div>
                  <p className="text-sm text-muted-foreground">Check back soon for new services</p>
                </div>
            }
            </div>
          </div>

          {/* Help Section */}
          <div className="bg-background py-12 md:py-16">
            <div className="max-w-[1440px] mx-auto px-4 md:px-6 lg:px-8">
              <div className="p-6 md:p-8 rounded-xl bg-primary/5 border border-primary/20">
                <div className="max-w-3xl mx-auto text-center">
                  <h2 className="text-2xl md:text-3xl font-bold text-foreground mb-3">
                    Need Help Finding the Right Service?
                  </h2>
                  <p className="text-base md:text-lg text-muted-foreground mb-6">
                    Our OTAssist team is here to help you find the perfect service for your needs. All enquiries are handled by our expert team.
                  </p>
                  <div className="flex flex-col sm:flex-row gap-4 justify-center">
                    <a
                    href="tel:+917039149001"
                    className="inline-flex items-center justify-center gap-2 px-6 py-3 bg-primary text-primary-foreground rounded-lg hover:bg-primary/90 transition-colors font-medium">

                      <Icon name="Phone" size={20} />
                      Call: +91 7039149001
                    </a>
                    <a
                    href="https://wa.me/917039149001"
                    target="_blank"
                    rel="noopener noreferrer"
                    className="inline-flex items-center justify-center gap-2 px-6 py-3 bg-card border border-border text-foreground rounded-lg hover:border-primary hover:text-primary transition-colors font-medium">

                      <Icon name="MessageCircle" size={20} />
                      WhatsApp Us
                    </a>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </>
      }
    </div>
  );

};

export default ServiceCategoryBrowse;